/**
 * Create or edit an announcement, in three steps.
 *
 * 1. **Contexto** — which tenants, and each one's version, page and use case.
 * 2. **Conteúdo** — the copy and the items, as fields or as JSON, kept in sync.
 * 3. **Revisar** — what will be created. Always a DRAFT; publishing is the approval flow's job.
 *
 * Several tenants can be picked at once, and **each keeps its own content**: the BFF has no
 * multi-tenant create, so this fans out into one create per tenant with its own version, page,
 * use case and document.
 *
 * The whole flow's state lives here, so stepping back and forward never clears anything.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import { useCallback, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantFlags } from '../../Hooks/useTenantFlags';
import { createWhatsNew, updateWhatsNew } from '../../Services/WhatsNew.service';
import type { WhatsNew } from '../../Types/WhatsNew.types';
import type { Language, WhatsNewFormData } from '../../Types/WhatsNewForm.types';
import type { TenantTarget } from '../../Utils/buildPayload';
import {
  buildMultiTenantPayloads,
  buildUpdatePayload,
  countPlannedRecords,
} from '../../Utils/buildPayload';
import type { WhatsNewContent } from '../../Utils/contentJson';
import {
  createEmptyContent,
  isContentUsable,
  parseContent,
  serializeContent,
} from '../../Utils/contentJson';
import { mapWhatsNewToFormData } from '../../Utils/mapWhatsNewToFormData';
import { SchemaModal } from './Components/SchemaModal';
import { StepContent } from './Steps/StepContent';
import { StepReview } from './Steps/StepReview';
import { TenantTargetRow } from './Steps/TenantTargetRow';
import { useActionToast } from '@/Hooks/useActionToast';
import { useTenants } from '@/Hooks/useTenants';
import { useTenantPages } from '@/Pages/BUs/Pages/Hooks/useTenantPages';
import { WhatsNewPreview } from '@/Shared/brand';
import { StepNav } from '@/Shared/components/StepNav/StepNav';
import { Tag } from '@/Shared/components/Tag/Tag';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const layoutStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-ml);
  align-items: flex-start;
`;

const bannerStyle = css`
  flex: 1 1 100%;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-border);
  border-left: 3px solid var(--color-primary);
  border-radius: var(--radius-m);
  background: var(--color-primary-subtle);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const panelStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  padding: var(--space-l);
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  flex: 100 1 340px;
  min-width: 0;
`;

const stepHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const stepTitleStyle = css`
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
`;

const stepSubtitleStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const fieldGroupStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const groupLabelStyle = css`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const chipsStyle = css`
  display: flex;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const helpStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const spacerStyle = css`
  flex: 1;
`;

const STEPS = [
  { number: 1, title: 'Contexto', hint: 'Tenants, versão e página de cada um' },
  { number: 2, title: 'Conteúdo', hint: 'Campos ou JSON, com pré-visualização' },
  { number: 3, title: 'Revisar', hint: 'Cria como rascunho' },
];

/** Content plus the JSON text, so invalid text survives a round trip through the fields tab. */
interface TenantDocument {
  content: WhatsNewContent;
  json: string;
  jsonError: string | null;
}

function createDocument(content: WhatsNewContent): TenantDocument {
  return { content, json: serializeContent(content), jsonError: null };
}

export interface WhatsNewCreateProps {
  onBack: () => void;
  onSuccess: () => void;
  /** The announcement being edited, if any. */
  editing?: WhatsNew;
}

const WhatsNewCreate = ({ onBack, onSuccess, editing }: WhatsNewCreateProps): JSX.Element => {
  const navigate = useNavigate();
  const isEditing = editing !== undefined;
  const { tenants } = useTenants();
  const toast = useActionToast();

  const [step, setStep] = useState(1);
  const [language, setLanguage] = useState<Language>('pt');
  const [tab, setTab] = useState<'fields' | 'json'>('fields');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const seededForm: WhatsNewFormData | undefined = editing
    ? mapWhatsNewToFormData(editing)
    : undefined;

  const [targets, setTargets] = useState<Record<string, TenantTarget>>(() =>
    editing
      ? {
          [editing.tenantSlug]: {
            platformVersionId: editing.platformVersion?.id ?? '',
            pageId: editing.pageId ?? undefined,
            useCaseSlugs: editing.useCaseSlug ? [editing.useCaseSlug] : [],
          },
        }
      : {},
  );

  const [documents, setDocuments] = useState<Record<string, TenantDocument>>(() =>
    editing && seededForm
      ? {
          [editing.tenantSlug]: createDocument({
            welcomeMessage: seededForm.welcomeMessage,
            subtitle: seededForm.subtitle,
            footer: seededForm.footer,
            confirmButtonText: seededForm.confirmButtonText,
            items: seededForm.items,
            actionParams: seededForm.actionParams,
          }),
        }
      : {},
  );

  const [activeTenant, setActiveTenant] = useState<string>(editing?.tenantSlug ?? '');

  const selectedTenants = useMemo(() => Object.keys(targets), [targets]);
  const activeDocument = documents[activeTenant];

  const activeTenantRecord = tenants.find(tenant => tenant.slug === activeTenant);
  const flags = useTenantFlags(activeTenantRecord?.flagsmithEnvironmentKey);
  const { pages: activePages } = useTenantPages({ tenantSlug: activeTenant });

  const toggleTenant = useCallback(
    (slug: string) => {
      setTargets(current => {
        // An announcement always has at least one tenant; the last one cannot be unpicked.
        if (slug in current) {
          if (Object.keys(current).length === 1) return current;
          const { [slug]: _removed, ...rest } = current;
          return rest;
        }
        return { ...current, [slug]: { platformVersionId: '', useCaseSlugs: [] } };
      });

      setDocuments(current => {
        if (slug in current) {
          if (Object.keys(current).length === 1) return current;
          const { [slug]: _removed, ...rest } = current;
          return rest;
        }
        // A tenant added later starts from a copy of whatever is being edited now.
        const source = current[activeTenant]?.content ?? createEmptyContent();
        return { ...current, [slug]: createDocument(structuredClone(source)) };
      });

      setActiveTenant(current => {
        if (current === slug) {
          const remaining = Object.keys(targets).filter(entry => entry !== slug);
          return remaining[0] ?? '';
        }
        return current || slug;
      });
    },
    [activeTenant, targets],
  );

  const updateTarget = useCallback((slug: string, target: TenantTarget) => {
    setTargets(current => ({ ...current, [slug]: target }));
  }, []);

  /** A field edit rewrites the JSON; the JSON is regenerated, never patched. */
  const setContent = useCallback(
    (next: WhatsNewContent) => {
      setDocuments(current => ({
        ...current,
        [activeTenant]: { content: next, json: serializeContent(next), jsonError: null },
      }));
    },
    [activeTenant],
  );

  /** Typed JSON is kept verbatim; only a parseable document updates the fields. */
  const setJson = useCallback(
    (raw: string) => {
      const result = parseContent(raw);

      setDocuments(current => {
        const existing = current[activeTenant];
        if (!existing) return current;

        return {
          ...current,
          [activeTenant]: result.ok
            ? { content: result.content, json: raw, jsonError: null }
            : { ...existing, json: raw, jsonError: result.message },
        };
      });
    },
    [activeTenant],
  );

  const copyToOthers = useCallback(() => {
    setDocuments(current => {
      const source = current[activeTenant];
      if (!source) return current;

      return Object.fromEntries(
        Object.entries(current).map(([slug, document]) => [
          slug,
          slug === activeTenant ? document : createDocument(structuredClone(source.content)),
        ]),
      );
    });
  }, [activeTenant]);

  const plannedRecords = useMemo(() => countPlannedRecords(targets), [targets]);

  const readyTargets = selectedTenants.filter(
    slug => targets[slug].platformVersionId.length > 0,
  ).length;
  const canLeaveContext = selectedTenants.length > 0 && readyTargets === selectedTenants.length;
  const canLeaveContent = selectedTenants.every(slug =>
    isContentUsable(documents[slug]?.content ?? createEmptyContent()),
  );

  const handleSubmit = async () => {
    setIsSaving(true);
    setSubmitError(null);

    try {
      if (editing) {
        const target = targets[editing.tenantSlug];
        const document = documents[editing.tenantSlug];
        await updateWhatsNew(editing.id, {
          ...buildUpdatePayload(
            { ...(seededForm as WhatsNewFormData), ...document.content },
            target.useCaseSlugs[0] ?? null,
          ),
          platformVersionId: target.platformVersionId || editing.platformVersion.id,
          ...(target.pageId ? { pageId: target.pageId } : {}),
        });
        toast.success('Anúncio salvo');
        onSuccess();
        return;
      }

      // Sequential on purpose: a partial failure should stop rather than fan out.
      // Each tenant's own document is expanded over its own use cases.
      for (const slug of selectedTenants) {
        const payloads = buildMultiTenantPayloads(
          {
            ...documents[slug].content,
            publishedAt: new Date().toISOString(),
          } as WhatsNewFormData,
          { [slug]: targets[slug] },
        );

        for (const payload of payloads) {
          await createWhatsNew(payload);
        }
      }

      toast.success(
        selectedTenants.length > 1
          ? `Anúncio criado em ${selectedTenants.length} BUs`
          : 'Anúncio criado',
        'Ele entra como rascunho e ainda precisa ser enviado para aprovação.',
      );
      onSuccess();
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : `Não foi possível ${editing ? 'salvar' : 'criar'} o anúncio.`,
      );
      toast.failure(`Não foi possível ${editing ? 'salvar' : 'criar'} o anúncio`, error);
    } finally {
      setIsSaving(false);
    }
  };

  const reviewRows = useMemo(() => {
    const first = selectedTenants[0];
    const target = first ? targets[first] : undefined;
    const document = first ? documents[first] : undefined;
    const page = activePages.find(entry => entry.id === target?.pageId);

    return [
      { label: 'Tenants', value: selectedTenants.join(', ') || '—' },
      {
        label: 'Versão',
        value:
          selectedTenants.length > 1
            ? 'uma por tenant'
            : target?.platformVersionId
              ? 'definida'
              : '—',
      },
      {
        label: 'Página',
        value:
          selectedTenants.length > 1
            ? 'uma por tenant'
            : page
              ? `${page.name} (${page.slug})`
              : 'principal do tenant',
      },
      {
        label: 'Use cases',
        value:
          selectedTenants
            .flatMap(slug =>
              targets[slug].useCaseSlugs.map(useCase =>
                selectedTenants.length > 1 ? `${slug}: ${useCase}` : useCase,
              ),
            )
            .join(', ') || 'todos os usuários',
      },
      { label: 'Registros', value: String(plannedRecords) },
      { label: 'Itens', value: String(document?.content.items.length ?? 0) },
      { label: 'Idiomas', value: 'Português, Inglês, Espanhol' },
    ];
  }, [selectedTenants, targets, documents, activePages, plannedRecords]);

  const primaryLabel =
    step < 3
      ? 'Continuar'
      : isSaving
        ? 'Salvando...'
        : isEditing
          ? 'Salvar alterações'
          : plannedRecords > 1
            ? `Criar ${plannedRecords} rascunhos`
            : 'Criar rascunho';

  return (
    <MainContent id='whats-new-create' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={layoutStyle}>
        {editing ? (
          <div className={bannerStyle}>
            <GLIcon variant='Settings.PencilSimple' size={16} fill='var(--color-primary)' />
            <span>Editando &quot;{editing.welcomeMessage}&quot;</span>
            <div className={spacerStyle} />
            <UTButton
              fitContent
              variant='tertiary'
              size='small'
              text='Descartar alterações'
              onClick={onBack}
            />
          </div>
        ) : null}

        <StepNav steps={STEPS} currentStep={step} />

        <section className={panelStyle}>
          {step === 1 ? (
            <>
              <div className={stepHeaderStyle}>
                <h2 className={stepTitleStyle}>Contexto do anúncio</h2>
                <p className={stepSubtitleStyle}>Onde esse What&apos;s New vai aparecer.</p>
              </div>

              <div className={fieldGroupStyle}>
                <span className={groupLabelStyle}>Tenants</span>
                <div className={chipsStyle}>
                  {tenants.map(tenant => (
                    <Tag
                      key={tenant.slug}
                      tone='primary'
                      selected={tenant.slug in targets}
                      disabled={isEditing}
                      onClick={
                        isEditing
                          ? undefined
                          : () => {
                              toggleTenant(tenant.slug);
                            }
                      }
                    >
                      {tenant.slug}
                    </Tag>
                  ))}
                </div>
                <span className={helpStyle}>
                  {isEditing
                    ? 'O tenant não muda na edição — publicar em outro tenant é um registro novo.'
                    : 'Escolha mais de um para publicar o mesmo anúncio em vários tenants — o BFF cria um registro por tenant.'}
                </span>
              </div>

              {selectedTenants.length > 0 ? (
                <div className={fieldGroupStyle}>
                  <span className={groupLabelStyle}>Destino por tenant</span>
                  {selectedTenants.map(slug => (
                    <TenantTargetRow
                      key={slug}
                      tenantSlug={slug}
                      target={targets[slug]}
                      useCases={tenants.find(tenant => tenant.slug === slug)?.useCases ?? []}
                      hasFlagKey={Boolean(
                        tenants.find(tenant => tenant.slug === slug)?.flagsmithEnvironmentKey,
                      )}
                      canRemove={selectedTenants.length > 1 && !isEditing}
                      onChange={updateTarget}
                      onRemove={toggleTenant}
                    />
                  ))}
                </div>
              ) : null}
            </>
          ) : null}

          {step === 2 && activeDocument ? (
            <StepContent
              content={activeDocument.content}
              jsonText={activeDocument.json}
              jsonError={activeDocument.jsonError}
              tab={tab}
              language={language}
              tenantSlugs={selectedTenants}
              activeTenant={activeTenant}
              environmentKey={activeTenantRecord?.flagsmithEnvironmentKey}
              flags={flags.flags}
              isLoadingFlags={flags.isLoading}
              isMissingKey={flags.isMissingKey}
              pageOptions={activePages
                .filter(page => page.isActive)
                .map(page => ({ value: page.slug, label: `${page.name} (${page.slug})` }))}
              onTabChange={setTab}
              onLanguageChange={setLanguage}
              onActiveTenantChange={setActiveTenant}
              onJsonChange={setJson}
              onContentChange={setContent}
              onCopyToOthers={copyToOthers}
              onPreview={() => {
                setIsPreviewOpen(true);
              }}
              onShowSchema={() => {
                setIsSchemaOpen(true);
              }}
              onReloadFlags={flags.refetch}
              onOpenBU={() => {
                navigate('/business-units');
              }}
            />
          ) : null}

          {step === 3 ? <StepReview rows={reviewRows} error={submitError} /> : null}

          <div className={footerStyle}>
            <UTButton fitContent variant='tertiary' text='Cancelar' onClick={onBack} />
            <UTButton
              fitContent
              variant='secondary'
              text='Voltar'
              disabled={step === 1}
              onClick={() => {
                setStep(current => Math.max(1, current - 1));
              }}
            />

            <div className={spacerStyle} />

            {step < 3 ? (
              <UTButton
                fitContent
                id='whats-new-create-next'
                text={primaryLabel}
                disabled={step === 1 ? !canLeaveContext : !canLeaveContent}
                onClick={() => {
                  setStep(current => current + 1);
                }}
              />
            ) : (
              <UTButton
                fitContent
                id='whats-new-create-submit'
                text={primaryLabel}
                disabled={!canLeaveContext || !canLeaveContent || isSaving}
                onClick={() => {
                  void handleSubmit();
                }}
              />
            )}
          </div>
        </section>
      </div>

      {isSchemaOpen ? (
        <SchemaModal
          onClose={() => {
            setIsSchemaOpen(false);
          }}
        />
      ) : null}

      {isPreviewOpen && activeDocument ? (
        <WhatsNewPreview
          tenantSlug={activeTenant}
          open
          title={activeDocument.content.welcomeMessage[language]}
          subtitle={activeDocument.content.subtitle[language] || undefined}
          footerMessage={activeDocument.content.footer[language] || undefined}
          items={activeDocument.content.items.map((item, index) => ({
            id: String(index),
            icon: item.icon as never,
            title: item.translations[language].title,
            body: item.translations[language].body,
            color: item.color,
            order: item.order,
          }))}
          actionButton={{
            text: activeDocument.content.confirmButtonText[language] || 'Entendi',
            onClick: () => {
              setIsPreviewOpen(false);
            },
          }}
          onClose={() => {
            setIsPreviewOpen(false);
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { WhatsNewCreate };
